// Pure helpers of « Connecteurs du bureau » (web/ui/myconnectors.js, its add panel
// myconnectors/add.js, its actions myconnectors/actions.js, its connect window
// myconnectors/connect.js) and of the library connectors of Administration → Connecteurs: the
// normalisation of GET /api/connectors/library and of its records, the live events, the store
// slice updates, the state and actions shown for each connector, the office's other connectors
// (detected: claude.ai, the server) as the page lists them, the checks that mirror the server for
// a custom connector (name, https address) and for the pasted callback address, the login page
// link and the job texts; for the plugins, their forms, the Shopify addresses the browser may be
// sent to, the return from Shopify and an arrival from it (a Google Drive's own helpers:
// myconnectors/drive-model.js). No DOM. Tested in test/ui-myconnectors.test.js,
// test/ui-library-plugins.test.js and test/ui-drive.test.js.
import { t } from '../../i18n/fr.js';
import { isSafeUrlAttr } from '../../dom.js';
import { connectorLabel, formatDateTime } from '../../format.js';
import { unwrapJob, localJob } from '../admin/accounts-model.js';
import { LIBRARY_KEY_RE, isLibraryKey, canUseConnector, isMaeckOffice } from '../connectorpolicy.js';
import { OFFICE_TABLES_KEY } from '../connectoricons.js';
import { DRIVE_MODES, GDRIVE_ID, driveAccountOf, driveDetails, driveFolderOf } from './drive-model.js';
import './strings.js';

/** The library's routes (server/modules/personal-connectors.js; /api/me/connectors is an alias). */
export const LIBRARY_BASE = '/api/connectors/library';
/** Connector states (server/modules/personal-connectors.js). */
export const PC_STATES = ['added', 'connected', 'needs-auth', 'error'];
/** Job states a connect window shows (the login job of server/engines/mcp-login.js). */
export const PC_JOB_STATES = ['starting', 'waiting_code', 'checking', 'done', 'error', 'expired', 'cancelled'];
/**
 * Caps when the server does not say (server/connectors/personal-catalogue.js LIBRARY_LIMITS):
 * connectors in the office's library, connectors one member adds (none for an administrator).
 */
export const LIBRARY_LIMITS = Object.freeze({ perOffice: 50, perMember: 10 });
/** Same limit as the server for a display name. */
export const LABEL_MAX = 60;
/** Same limit as the server for a custom connector address. */
export const URL_MAX = 2000;
/** Longest pasted callback address accepted. */
export const CALLBACK_MAX = 4096;
/** A library connector id (= its server name for Claude Code): never the name of whoever added it. */
export const LIBRARY_ID_RE = LIBRARY_KEY_RE;
/**
 * Catalogue services the office serves itself (server/connectors/plugins/): no login through
 * Claude Code — a key (Mailgun, Browserbase), a key pair (Vmake), a token (GitHub), « Se connecter avec Shopify » (the owner's app, or the Shopify
 * CLI's login in the connect window) / a pasted token (Shopify), « Se connecter avec Google » and
 * its folder (Google Drive: myconnectors/drive.js).
 */
export const PLUGIN_IDS = Object.freeze(['shopify', 'mailgun', GDRIVE_ID, 'github', 'browserbase', 'vmake']);
/** Mailgun regions (server/connectors/plugins/mailgun.js). */
export const MAILGUN_REGIONS = Object.freeze(['us', 'eu']);
/** Same checks as the server's plugins (it checks again: it is the real guard). */
const MAILGUN_KEY_RE = /^[A-Za-z0-9_-]{20,200}$/;
const DOMAIN_RE = /^(?=.{3,253}$)[A-Za-z0-9](?:[A-Za-z0-9-]{0,62}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,62}[A-Za-z0-9])?)+$/;
const SHOP_RE = /^[a-z0-9][a-z0-9-]{0,60}\.myshopify\.com$/;
const SHOPIFY_TOKEN_RE = /^shp[a-z]{2}_[A-Za-z0-9]{16,200}$/;
const GITHUB_TOKEN_RE = /^(?:gh[pous]_[A-Za-z0-9]{30,255}|github_pat_[A-Za-z0-9_]{40,255})$/;
const BROWSERBASE_KEY_RE = /^[A-Za-z0-9_-]{20,200}$/;
const VMAKE_KEY_RE = /^[A-Za-z0-9_+/=.-]{8,256}$/;
/**
 * The outcomes of a return from Shopify (`#/connectors/<id>?shopify=…`, server's shopifyCallback)
 * and of an arrival from Shopify (`#/connectors?shopify=launch&l=<id>`, server's shopifyLaunch).
 */
export const SHOPIFY_OUTCOMES = Object.freeze(['ok', 'error', 'expired', 'denied', 'login', 'gone', 'launch', 'launch-invalid', 'launch-expired']);
/** An arrival from Shopify waiting for the person's choice (server's launch id). */
const LAUNCH_ID_RE = /^[A-Za-z0-9_-]{20,100}$/;
/** The paths of a store's own admin that « Se connecter avec Shopify » may open (authorize, install). */
const SHOP_OAUTH_PATHS = ['/admin/oauth/authorize', '/admin/oauth/install_custom_app', '/admin/oauth/install'];

/** The path of the page that does not load after a login: Claude Code's (Shopify's CLI says it in its address). */
const CALLBACK_PATH = '/callback';
/** Loopback names the CLI's callback page may use (some browsers upgrade it to https). */
const CALLBACK_HOSTS = ['localhost', '127.0.0.1', '[::1]'];
const CALLBACK_PROTOCOLS = ['http:', 'https:'];
// eslint-disable-next-line no-control-regex
const CONTROL_RE_G = /[\u0000-\u001f\u007f-\u009f\u2028\u2029]+/g;
// eslint-disable-next-line no-control-regex
const SPACE_OR_CONTROL_RE = /[\s\u0000-\u001f\u007f-\u009f]/;
const TONES = {
  added: ['neutral', 'off'], connected: ['success', 'ok'], 'needs-auth': ['amber', 'warn'], error: ['danger', 'error'],
};

function str(v) {
  return typeof v === 'string' ? v : v == null ? '' : String(v);
}

/** A short single line of text (no control characters, capped). */
function line(v, max = 300) {
  const s = typeof v === 'string' ? v.replace(CONTROL_RE_G, ' ').replace(/\s+/g, ' ').trim() : '';
  return s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s;
}

function count(v) {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

function countOrNull(v) {
  const n = Number(v);
  return v !== null && v !== undefined && v !== '' && Number.isInteger(n) && n >= 0 ? n : null;
}

function dateOf(v) {
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  return typeof v === 'string' && v && Number.isFinite(Date.parse(v)) ? v : null;
}

/** The host of an address ('' when it does not parse). */
function hostOf(url) {
  try { return new URL(str(url)).hostname.toLowerCase(); } catch { return ''; }
}

// ---------------------------------------------------------------------------------------------
// Records and the store slice

/**
 * Normalises a library connector as the server sends it (a bare record or `{connector}`).
 * `mine` / `canManage` / `allowedForMe` are the caller's (GET and the actions' answers carry them;
 * a live event does not: liveConnectorOf). `url` and `tools` come only to whoever may manage it.
 * @param {unknown} raw
 * @returns {{id: string, label: string, catalogId: string|null, url: string, host: string,
 *   status: string, tools: Array<{name: string, kind: 'read'|'write', overridden: boolean}>, toolCount: number,
 *   readCount: number|null, writeCount: number|null, addedBy: string|null, addedByName: string,
 *   addedAt: string|number|null, connectedAt: string|number|null, checkedAt: string|number|null,
 *   lastError: string, mine: boolean, canManage: boolean, allowedForMe: boolean, auth?: string|null,
 *   shop?: string|null, storage?: boolean}|null} null without an id; auth / shop: a Shopify store's, when the server
 *   says them (how it connects: 'cli' | 'oauth' | 'token' | null; its store); storage: a Google
 *   Drive's, when the server says it (it stores the office's files: everyone sees it)
 */
export function connectorOf(raw) {
  const r = raw && typeof raw === 'object' && !Array.isArray(raw) && raw.connector && typeof raw.connector === 'object' ? raw.connector : raw;
  if (!r || typeof r !== 'object' || Array.isArray(r) || typeof r.id !== 'string' || !r.id) return null;
  const tools = Array.isArray(r.tools)
    ? r.tools.filter((x) => x && typeof x.name === 'string' && x.name)
      .map((x) => ({ name: line(x.name, 120), kind: x.kind === 'read' ? 'read' : 'write', overridden: x.overridden === true }))
    : [];
  const readCount = countOrNull(r.readCount);
  const writeCount = countOrNull(r.writeCount);
  const plugin = pluginSettingsOf(r.plugin);
  // A Shopify store says to everyone how it connects and its store (the live events too).
  const shopify = r.catalogId === 'shopify' && ('auth' in r || 'shop' in r)
    ? { auth: typeof r.auth === 'string' && r.auth ? line(r.auth, 20) : null, shop: typeof r.shop === 'string' && SHOP_RE.test(r.shop) ? r.shop : null } : null;
  // A Google Drive says to everyone whether it stores the office's files (the live events too).
  const drive = r.catalogId === GDRIVE_ID && ('storage' in r || typeof plugin?.storage === 'boolean')
    ? { storage: 'storage' in r ? r.storage === true : plugin.storage === true } : null;
  return {
    ...(plugin ? { plugin } : {}),
    ...(shopify || {}),
    ...(drive || {}),
    id: r.id,
    label: line(r.label, 80) || r.id,
    catalogId: typeof r.catalogId === 'string' && r.catalogId ? r.catalogId : null,
    url: typeof r.url === 'string' ? r.url : '',
    host: line(r.host, 255) || hostOf(r.url),
    // An unknown state reads as « to connect »: the only action offered is to connect it again.
    status: PC_STATES.includes(r.status) ? r.status : 'added',
    tools,
    toolCount: tools.length || count(r.toolCount) || (readCount ?? 0) + (writeCount ?? 0),
    readCount: tools.length ? tools.filter((x) => x.kind === 'read').length : readCount,
    writeCount: tools.length ? tools.filter((x) => x.kind === 'write').length : writeCount,
    addedBy: typeof r.addedBy === 'string' && r.addedBy ? r.addedBy : typeof r.ownerId === 'string' && r.ownerId ? r.ownerId : null,
    addedByName: line(r.addedByName ?? r.ownerName, 80),
    addedAt: dateOf(r.addedAt ?? r.createdAt),
    connectedAt: dateOf(r.connectedAt),
    checkedAt: dateOf(r.checkedAt),
    lastError: line(r.lastError, 300),
    mine: r.mine === true,
    canManage: r.canManage === true,
    allowedForMe: r.allowedForMe !== false,
  };
}

/**
 * A plugin's settings as its managers see them (server: plugin.publicSettings; never a key nor a
 * token, their last 4 characters at most), or null.
 * @param {unknown} raw
 * @returns {{region?: string, domain?: string|null, keyHint?: string|null, login?: string|null, shop?: string|null, shopName?: string|null,
 *   apiVersion?: string|null, auth?: string|null, tokenHint?: string|null, scopes?: string|null,
 *   cliExpiresAt?: string|null, cliRenewable?: string|null, missingScopes?: string[], mode?: 'limited'|'full',
 *   account?: {email: string, name: string}|null, folder?: {id: string, name: string, url: string}|null, storage?: boolean}|null}
 *   mode / account / folder / storage: a Google Drive's (its access, its Google account, its folder,
 *   whether it stores the office's files)
 */
export function pluginSettingsOf(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out = {};
  // cliExpiresAt / cliRenewable: a store connected through the Shopify CLI (the end of its session,
  // whether Shopify renews it: 'yes' | 'no').
  for (const key of ['region', 'domain', 'keyHint', 'shop', 'shopName', 'apiVersion', 'auth', 'tokenHint', 'scopes', 'cliExpiresAt', 'cliRenewable', 'login', 'project']) {
    if (key in raw) out[key] = typeof raw[key] === 'string' && raw[key] ? line(raw[key], key === 'scopes' ? 4000 : 255) : null;
  }
  // A Google Drive: its access, its Google account, its folder, the office's files.
  if ('mode' in raw) out.mode = DRIVE_MODES.includes(raw.mode) ? raw.mode : 'limited';
  if ('account' in raw) out.account = driveAccountOf(raw.account);
  if ('folder' in raw) out.folder = driveFolderOf(raw.folder);
  if ('storage' in raw) out.storage = raw.storage === true;
  // The access a connected Shopify store has yet to grant (only when some).
  if (Array.isArray(raw.missingScopes)) {
    const list = raw.missingScopes.filter((x) => typeof x === 'string' && /^[a-z_]{1,60}$/.test(x)).slice(0, 100);
    if (list.length) out.missingScopes = list;
  }
  return out;
}

/** True for a connector the office serves itself (a plugin of the catalogue: Shopify, Mailgun, Google Drive). */
export function isPluginConnector(conn) {
  return PLUGIN_IDS.includes(conn?.catalogId) || conn?.kind === 'plugin';
}

/**
 * True for a Shopify store connected (or to connect) through the Shopify CLI: its CONNECTER /
 * RECONNECTER open the connect window (the CLI's login), like a remote connector's.
 * @param {object|null} conn connectorOf()
 */
export function isShopifyCli(conn) {
  return conn?.catalogId === 'shopify' && conn?.plugin?.auth === 'cli';
}

/**
 * How « Se connecter avec Shopify » goes in this office: the owner's app ('app'), else the
 * Shopify CLI ('cli': no app to create), else none (a pasted token only).
 * @param {{apps?: {shopify?: {ready?: boolean, cli?: boolean}}}|null} data normalizeLibrary()
 * @returns {'app'|'cli'|null}
 */
export function shopifyRoute(data) {
  if (data?.apps?.shopify?.ready === true) return 'app';
  return data?.apps?.shopify?.cli === true ? 'cli' : null;
}

/** The key (pc.action.settings.*) of a plugin's settings button: a CLI store's, a Google Drive's are its « Réglages ». */
export function settingsActionKey(conn) {
  if (isShopifyCli(conn)) return 'shopifyCli';
  if (conn?.catalogId === GDRIVE_ID) return 'gdrive';
  if (conn?.catalogId === 'github') return 'github';
  if (conn?.catalogId === 'browserbase') return 'browserbase';
  if (conn?.catalogId === 'vmake') return 'vmake';
  return conn?.catalogId === 'shopify' ? 'shopify' : 'mailgun';
}

/**
 * The connectors of a list answer (`{connectors}` or a bare array), normalised, without duplicates.
 * @param {unknown} res
 */
export function connectorsOf(res) {
  const list = Array.isArray(res) ? res : Array.isArray(res?.connectors) ? res.connectors : [];
  const seen = new Set();
  const out = [];
  for (const raw of list) {
    const c = connectorOf(raw);
    if (c && !seen.has(c.id)) { seen.add(c.id); out.push(c); }
  }
  return out;
}

/**
 * May `me` add connectors to the library (the server checks again: it is the real guard)? An
 * active administrator, or a member the admin allowed (« Peut ajouter des connecteurs »:
 * `canAddConnectors`, formerly `personalConnectors`).
 * @param {object|null} me
 */
export function canAddConnectors(me) {
  return !!me && !me.disabled && (me.role === 'admin' || me.canAddConnectors === true || me.personalConnectors === true);
}

/**
 * Who sees « Connecteurs » and what they may do there. Enabled: the server reports the library on
 * (features.connectorLibrary, or its former name personalConnectors). Shown: to every logged-in
 * person when enabled (everyone sees the office's connectors and which ones he may use). canAdd:
 * canAddConnectors(me).
 * @param {{features?: object, me?: object|null}} state the store
 * @returns {{enabled: boolean, canAdd: boolean, show: boolean}}
 */
export function libraryAccess(state) {
  const f = state?.features || {};
  const enabled = f.connectorLibrary === true || f.personalConnectors === true;
  const me = state?.me || null;
  return { enabled, canAdd: enabled && canAddConnectors(me), show: enabled && !!me && !me.disabled };
}

/**
 * May `me` manage (connect, check, rename, remove) connector `c`? An administrator any; a member
 * allowed to add, the ones he added (server/modules/personal-connectors.js mayManage).
 * @param {object} c connectorOf()
 * @param {object|null} me
 */
export function mayManage(c, me) {
  if (!me?.id || me.disabled || !c) return false;
  if (me.role === 'admin') return true;
  return canAddConnectors(me) && c.addedBy === me.id;
}

/**
 * The connector of a live `connector-library` event (every user receives it: never the address,
 * the tools nor the caller's own flags), with `me`'s flags — those of the copy held, else worked
 * out like the server does (mine, mayManage, the « Connecteurs autorisés »).
 * @param {unknown} raw the event data (`{connector}`)
 * @param {{me?: object|null}} state the store (me, for its policy)
 * @param {object[]} [list] the connectors held
 * @returns {object|null}
 */
export function liveConnectorOf(raw, state, list = []) {
  const c = connectorOf(raw);
  if (!c) return null;
  const me = state?.me || null;
  const held = (Array.isArray(list) ? list : []).find((x) => x.id === c.id);
  if (held) return { ...c, mine: held.mine, canManage: held.canManage, allowedForMe: held.allowedForMe };
  return { ...c, mine: !!me?.id && c.addedBy === me.id, canManage: mayManage(c, me), allowedForMe: canUseConnector(state, c.id) };
}

/**
 * Normalises GET /api/connectors/library.
 * @param {unknown} res
 * @returns {{enabled: boolean, canAdd: boolean, message: string,
 *   catalogue: Array<{id: string, label: string, description: string, host: string, kind: 'remote'|'plugin',
 *     available: boolean, unavailable: string}>,
 *   limits: {perOffice: number, perMember: number|null, office: number, mine: number},
 *   apps: {shopify: {ready: boolean, redirectUrl: string, cli: boolean, cliMessage: string},
 *     google: {ready: boolean, pickerReady: boolean}}, connectors: object[]}}
 *   apps.shopify: ready — the owner's app is set; cli — the Shopify CLI is installed (« Se connecter
 *   avec Shopify » without an app), else cliMessage says so; apps.google: the office's Google app is
 *   set (« Se connecter avec Google »), with the API key and project number of Google's folder
 *   picker (pickerReady)
 *   canAdd: the person may add connectors (`allowed`, the former name, is read too); message: why
 *   the feature is off or why the person may not add (French, from the server); limits.perMember:
 *   null for an administrator (no cap of his own)
 */
export function normalizeLibrary(res) {
  const r = res && typeof res === 'object' && !Array.isArray(res) ? res : {};
  const catalogue = (Array.isArray(r.catalogue) ? r.catalogue : [])
    .filter((c) => c && typeof c.id === 'string' && c.id)
    .map((c) => ({
      id: c.id, label: line(c.label, 80) || c.id, description: line(c.description, 400), host: line(c.host, 255).toLowerCase(),
      kind: c.kind === 'plugin' || PLUGIN_IDS.includes(c.id) ? 'plugin' : 'remote',
      available: c.available !== false,
      unavailable: c.available === false ? line(c.unavailable, 400) : '',
    }));
  const shopify = r.apps?.shopify && typeof r.apps.shopify === 'object' ? r.apps.shopify : {};
  const google = r.apps?.google && typeof r.apps.google === 'object' ? r.apps.google : {};
  const apps = {
    shopify: {
      ready: shopify.ready === true,
      redirectUrl: typeof shopify.redirectUrl === 'string' ? line(shopify.redirectUrl, 500) : '',
      cli: shopify.cli === true,
      cliMessage: line(shopify.cliMessage, 400),
    },
    google: { ready: google.ready === true, pickerReady: google.pickerReady === true },
  };
  const connectors = connectorsOf(Array.isArray(res) ? res : r.connectors);
  const enabled = r.enabled !== false;
  const canAdd = enabled && (typeof r.canAdd === 'boolean' ? r.canAdd : r.allowed !== false);
  const l = r.limits && typeof r.limits === 'object' ? r.limits : {};
  const limits = {
    perOffice: count(l.perOffice) || LIBRARY_LIMITS.perOffice,
    perMember: l.perMember === null ? null : count(l.perMember) || LIBRARY_LIMITS.perMember,
    office: countOrNull(l.office) ?? connectors.length,
    mine: countOrNull(l.mine) ?? connectors.filter((c) => c.mine).length,
  };
  return { enabled, canAdd, message: !canAdd ? line(r.message, 400) : '', catalogue, limits, apps, connectors };
}

/**
 * The plugin settings held for a connector, brought up to date with what a live event says of a
 * Shopify store (`auth`, `shop`): connected another way or to another store meanwhile (the CLI, the
 * owner's app, a token), what belonged to the former connection goes (its token's last
 * characters, its scopes, the end of a CLI session) until the settings are read again. A Google
 * Drive's `storage` follows the event.
 * @param {object} plugin the held settings
 * @param {object} c the event's record (connectorOf)
 */
function livePlugin(plugin, c) {
  if (c.catalogId === GDRIVE_ID) return typeof c.storage === 'boolean' && plugin.storage !== c.storage ? { ...plugin, storage: c.storage } : plugin;
  if (c.catalogId !== 'shopify' || !('auth' in c)) return plugin;
  const auth = c.auth ?? null;
  const shop = c.shop ?? null;
  if ((plugin.auth ?? null) === auth && (plugin.shop ?? null) === shop) return plugin;
  return {
    shop, shopName: shop === plugin.shop ? plugin.shopName ?? null : null, apiVersion: plugin.apiVersion ?? null, auth, tokenHint: null, scopes: null,
  };
}

/**
 * The list with `connector` inserted (at the end) or replaced. A record without address, tools or
 * plugin settings (the live events never carry them) keeps the ones already held (the tools only
 * while their count is the same; the plugin settings say how a Shopify store connects — a live
 * event of a Shopify store says it too, `auth` and `shop`: the held settings follow it). The same
 * list comes back when the copy is already held, so the shell and an open page can both apply one
 * live event.
 * @param {object[]} list
 * @param {object|null} connector a normalised record
 * @returns {object[]}
 */
export function withConnector(list, connector) {
  const cur = Array.isArray(list) ? list : [];
  if (!connector?.id) return cur;
  const i = cur.findIndex((c) => c.id === connector.id);
  if (i < 0) return [...cur, connector];
  const held = cur[i];
  let next = connector;
  if (!next.url && held.url && next.canManage) next = { ...next, url: held.url };
  if (!next.plugin && held.plugin && next.canManage) next = { ...next, plugin: livePlugin(held.plugin, next) };
  if (!next.tools?.length && held.tools?.length && held.tools.length === next.toolCount && next.canManage) {
    next = { ...next, tools: held.tools };
  }
  if (JSON.stringify(held) === JSON.stringify(next)) return cur;
  return cur.map((c, j) => (j === i ? next : c));
}

/** The list without `id` (the same list when it is not there). */
export function withoutConnector(list, id) {
  const cur = Array.isArray(list) ? list : [];
  return cur.some((c) => c.id === id) ? cur.filter((c) => c.id !== id) : cur;
}

/** How many connectors the person may fix wait for it (to reconnect, or in error): the user-menu hint. */
export function needsAttention(list) {
  let n = 0;
  for (const c of Array.isArray(list) ? list : []) if (c?.canManage && (c.status === 'needs-auth' || c.status === 'error')) n += 1;
  return n;
}

/**
 * Which limit stops a new connector, if any: the office's library is full, or (a member) the
 * connectors he added reached his own cap.
 * @param {{perOffice: number, perMember: number|null}} limits normalizeLibrary().limits
 * @param {object[]} list the whole library (connectorOf records)
 * @returns {{kind: 'office'|'member', max: number}|null}
 */
export function addLimit(limits, list) {
  const all = Array.isArray(list) ? list : [];
  const l = limits || LIBRARY_LIMITS;
  const perOffice = count(l.perOffice) || LIBRARY_LIMITS.perOffice;
  if (all.length >= perOffice) return { kind: 'office', max: perOffice };
  const perMember = l.perMember === null ? null : count(l.perMember) || LIBRARY_LIMITS.perMember;
  if (perMember !== null && all.filter((c) => c.mine).length >= perMember) return { kind: 'member', max: perMember };
  return null;
}

// ---------------------------------------------------------------------------------------------
// The office's other connectors (detected)

/**
 * The office's detected connectors as « Connecteurs » lists them, from the store's `connectors`
 * (bootstrap / GET /api/connectors summary): neither the library's (listed apart), nor the
 * built-in Tables du bureau, nor the admin connector; sorted by name; with whether the agents may
 * use each one for this person (his « Connecteurs autorisés »).
 * @param {{connectors?: object[], me?: object|null}} state the store
 * @returns {Array<{key: string, name: string, status: string, engine: string, toolCount: number|null,
 *   readCount: number|null, writeCount: number|null, allowedForMe: boolean, server: object}>}
 */
export function detectedConnectors(state) {
  const list = Array.isArray(state?.connectors) ? state.connectors : [];
  return list
    .filter((s) => s && typeof s.key === 'string' && s.key && s.library !== true && !isLibraryKey(s.key)
      && s.builtin !== true && s.key !== OFFICE_TABLES_KEY && !isMaeckOffice(s.key) && !isMaeckOffice(s.name))
    .map((s) => {
      const readCount = countOrNull(s.readCount);
      const writeCount = countOrNull(s.writeCount);
      return {
        key: s.key,
        name: connectorLabel(s.name || s.key) || s.key,
        status: typeof s.status === 'string' && s.status ? s.status : 'unknown',
        engine: s.engine === 'codex' || s.key.startsWith('codex-') ? 'codex' : 'claude',
        toolCount: countOrNull(s.toolCount) ?? (readCount !== null || writeCount !== null ? (readCount ?? 0) + (writeCount ?? 0) : null),
        readCount,
        writeCount,
        allowedForMe: canUseConnector(state, s.key),
        server: s,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }));
}

// ---------------------------------------------------------------------------------------------
// Display

/**
 * « Ajouté par vous », « Ajouté par Léa », or « Ajouté dans le bureau » when the server does not
 * say who.
 * @param {object} conn connectorOf()
 */
export function addedByText(conn) {
  if (conn?.mine) return t('pc.addedByYou');
  return conn?.addedByName ? t('pc.addedBy', { name: conn.addedByName }) : t('pc.addedByUnknown');
}

/**
 * The key (pc.textPlugin.shopifyAdded.*) of what a Shopify store still to connect (not a CLI one)
 * says, by the way « Se connecter » goes here: the owner's app — the store picked in Shopify when
 * none is known yet, else its access accepted —, the Shopify CLI — the store's Shopify page, its
 * address asked first when none is known —, a pasted token; `any` while the way is not known.
 * @param {object} conn connectorOf()
 * @param {'app'|'cli'|null|undefined} route shopifyRoute()
 */
function shopifyAddedKey(conn, route) {
  const known = Boolean(conn?.plugin?.shop || conn?.shop || SHOP_RE.test(str(conn?.host)));
  if (route === 'app') return known ? 'app' : 'pick';
  if (route === 'cli') return known ? 'cli' : 'cliShop';
  return route === null ? 'token' : 'any';
}

/**
 * What a row shows for a library connector.
 * @param {object} conn connectorOf()
 * @param {{jobActive?: boolean, canManage?: boolean, shopifyRoute?: 'app'|'cli'|null}} [opts] jobActive:
 *   a login of this connector still runs (this page started it); canManage: the person may
 *   connect, check, rename and remove it (default: the record's `canManage`) — else no action at
 *   all, and the texts say who can fix it; shopifyRoute: how « Se connecter avec Shopify » goes in
 *   this office (shopifyRoute(); absent: not known yet), what a Shopify store to connect says
 * @returns {{status: string, tone: string, dot: string, badge: string, text: string, lead?: 'lock'|'folder'|null,
 *   needsFolder?: boolean, primaryIcon?: 'key'|'folder', primary: 'connect'|'reconnect'|'resume'|null, reconnect: boolean,
 *   check: boolean, manage: boolean}}
 *   lead: the icon shown instead of the status dot (a Google Drive limited to its folder: a lock);
 *   needsFolder: a Google Drive connected without its folder (amber, DOSSIER À CHOISIR); primaryIcon:
 *   the icon of the primary button (CHOISIR LE DOSSIER: a folder);
 *   reconnect: a quieter « Reconnecter » for a connected one (asked first: a new login drops the
 *   current one); check: « Vérifier », in every state but while a login runs (a server that needs
 *   no login is connected that way from À CONNECTER); manage: Renommer and Retirer
 */
export function stateView(conn, { jobActive = false, canManage = conn?.canManage === true, shopifyRoute: route } = {}) {
  const status = PC_STATES.includes(conn?.status) ? conn.status : 'added';
  const [tone, dot] = TONES[status];
  const plugin = isPluginConnector(conn);
  let text;
  const cli = isShopifyCli(conn);
  const drive = conn?.catalogId === GDRIVE_ID;
  // A Google Drive connected: the folder it is limited to (a lock), or the folder still to choose
  // (its agents can do nothing there yet: CHOISIR LE DOSSIER comes first, never « all done »). Said
  // to whoever the server tells its folder (the folder's name is no secret).
  const driveFolder = drive && status === 'connected' && conn.plugin && 'folder' in conn.plugin ? conn.plugin.folder : undefined;
  const needsFolder = driveFolder === null;
  let lead = null;
  if (needsFolder) text = t('pc.textPlugin.gdrive.noFolder');
  else if (driveFolder) {
    const full = conn.plugin.mode === 'full';
    const account = conn.plugin.account?.email || conn.plugin.account?.name || '';
    text = [t(full ? 'pc.textPlugin.gdrive.full' : 'pc.textPlugin.gdrive.limited', { folder: driveFolder.name || driveFolder.id }), account].filter(Boolean).join(' · ');
    lead = full ? 'folder' : 'lock';
  } else if (status === 'connected') text = toolsText(conn) || t('pc.text.connected');
  else if (!canManage) text = t(`pc.textOther.${status}`);
  else if (status === 'error' || (plugin && conn?.lastError)) text = conn?.lastError || t('pc.text.error');
  else if (plugin && !cli && conn.catalogId === 'shopify' && status === 'added') text = t(`pc.textPlugin.shopifyAdded.${shopifyAddedKey(conn, route)}`);
  else text = t(plugin ? `pc.textPlugin.${cli ? 'shopifyCli' : drive ? 'gdrive' : conn.catalogId === 'shopify' ? 'shopify' : 'key'}.${status}` : `pc.text.${status}`);
  if (cli) {
    // A Shopify store through the Shopify CLI: CONNECTER / RECONNECTER are its login (the connect
    // window); its settings (another store, a pasted token) stay at hand. Connected without all
    // the access the office asks now: RECONNECTER comes first.
    const more = shopifyMissingScopes(conn).length > 0;
    return {
      status, tone, dot,
      badge: t(`pc.state.${status}`),
      text,
      plugin: true,
      primary: !canManage ? null : jobActive ? 'resume' : status === 'added' ? 'connect' : status === 'connected' ? (more ? 'reconnect' : null) : 'reconnect',
      settingsLabel: null,
      settings: canManage && !jobActive,
      reconnect: canManage && status === 'connected' && !jobActive && !more,
      check: canManage && !jobActive,
      manage: canManage,
    };
  }
  if (plugin) {
    // A plugin never logs in through Claude Code: its settings window (key, store, token; a
    // Google Drive's connection, folder and storage — CHOISIR LE DOSSIER while it has none, amber:
    // not done yet). A Google Drive never connected has nothing to check yet.
    return {
      status,
      tone: needsFolder ? 'amber' : tone,
      dot: needsFolder ? 'warn' : dot,
      badge: needsFolder ? t('pc.drive.state.needsFolder') : t(`pc.state.${status}`),
      text,
      lead,
      needsFolder,
      plugin: true,
      primary: canManage && (status !== 'connected' || needsFolder) ? 'settings' : null,
      settingsLabel: needsFolder ? 'googleFolder' : pluginActionKey(conn, status),
      primaryIcon: needsFolder ? 'folder' : 'key',
      settings: canManage && status === 'connected' && !needsFolder,
      reconnect: false,
      check: canManage && !(drive && status === 'added'),
      manage: canManage,
    };
  }
  return {
    status, tone, dot,
    badge: t(`pc.state.${status}`),
    text,
    plugin: false,
    primary: !canManage ? null : jobActive ? 'resume' : status === 'added' ? 'connect' : status === 'connected' ? null : 'reconnect',
    settingsLabel: null,
    settings: false,
    reconnect: canManage && status === 'connected' && !jobActive,
    check: canManage && !jobActive,
    manage: canManage,
  };
}

/**
 * The action key (pc.action.*) of a plugin's settings button: « SE CONNECTER » for a store (a
 * Google Drive) never connected, « RECONNECTER » for one to connect again or once connected,
 * « MODIFIER LA CLÉ » for a key.
 * @param {object} conn
 * @param {string} status
 */
export function pluginActionKey(conn, status) {
  if (conn?.catalogId === 'shopify') return status === 'added' ? 'shopifyConnect' : 'reconnect';
  if (conn?.catalogId === GDRIVE_ID) return status === 'added' ? 'googleConnect' : 'reconnect';
  return conn?.catalogId === 'vmake' ? 'editKeys' : 'editKey';
}

/**
 * The access a connected Shopify store has yet to grant (the office asks more than when it was
 * connected: « Reconnecter » grants it), [] when none or not known.
 * @param {object} conn
 * @returns {string[]}
 */
export function shopifyMissingScopes(conn) {
  const list = conn?.plugin?.missingScopes;
  return conn?.catalogId === 'shopify' && conn.status === 'connected' && Array.isArray(list) ? list.filter((x) => typeof x === 'string') : [];
}

/**
 * The details line of a plugin for its managers: « ma-boutique.myshopify.com · connecté par
 * Shopify (…ab12) », « Europe · mg.exemple.com · clé …ab12 », « marie@exemple.com · limité à un
 * dossier · dossier « ECOM USA » » ('' without settings).
 * @param {object} conn connectorOf()
 */
export function pluginDetails(conn, now = Date.now()) {
  const p = conn?.plugin;
  if (!p) return '';
  if (conn.catalogId === GDRIVE_ID) return driveDetails(p);
  const missing = shopifyMissingScopes(conn).length;
  const more = missing ? t('pc.plugin.newScopes', { n: missing }) : '';
  if (isShopifyCli(conn)) {
    // Shopify does not renew it: the person sees when it ends (to reconnect then).
    const end = p.cliRenewable === 'no' && p.cliExpiresAt ? formatDateTime(p.cliExpiresAt, now) : '';
    return [p.shop, t('pc.plugin.cliHint'), end ? t('pc.plugin.cliUntil', { when: end }) : '', more].filter(Boolean).join(' · ');
  }
  if (conn.catalogId === 'shopify') {
    return [p.shop, p.tokenHint ? t(p.auth === 'oauth' ? 'pc.plugin.oauthHint' : 'pc.plugin.tokenHint', { hint: p.tokenHint }) : '', more].filter(Boolean).join(' · ');
  }
  if (conn.catalogId === 'github') return [p.login ? `@${p.login}` : '', p.keyHint ? t('pc.plugin.tokenHint', { hint: p.keyHint }) : ''].filter(Boolean).join(' · ');
  if (conn.catalogId === 'browserbase') return [p.project || '', p.keyHint ? t('pc.plugin.keyHint', { hint: p.keyHint }) : ''].filter(Boolean).join(' · ');
  if (conn.catalogId === 'vmake') return p.keyHint ? t('pc.plugin.accessKeyHint', { hint: p.keyHint }) : '';
  return [t(`pc.plugin.region.${p.region === 'eu' ? 'eu' : 'us'}`), p.domain || '', p.keyHint ? t('pc.plugin.keyHint', { hint: p.keyHint }) : ''].filter(Boolean).join(' · ');
}

/** "12 outils (8 en lecture, 4 en écriture)", "3 outils", or '' when none is known. */
export function toolsText(conn) {
  const tools = Array.isArray(conn?.tools) ? conn.tools : [];
  const n = tools.length || count(conn?.toolCount);
  if (!n) return '';
  const label = t('pc.tools', { n });
  const read = tools.length ? tools.filter((x) => x.kind === 'read').length : countOrNull(conn?.readCount);
  const write = tools.length ? tools.length - read : countOrNull(conn?.writeCount);
  if (read === null || write === null) return label;
  return t('pc.toolsSplit', { tools: label, read, write });
}

function fold(s) {
  return str(s).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Library rows whose name, host or adder contain every word of `q` (accents and case ignored). */
export function filterLibrary(rows, q) {
  const words = fold(q).split(/\s+/).filter(Boolean);
  const list = Array.isArray(rows) ? rows : [];
  if (!words.length) return list;
  return list.filter((r) => {
    const hay = fold(`${r.label} ${r.host} ${r.addedByName}`);
    return words.every((w) => hay.includes(w));
  });
}

// ---------------------------------------------------------------------------------------------
// Checks mirrored from the server

/**
 * True for a host the office must never be pointed at: loopback, private and link-local address
 * literals, internal names (*.internal, *.railway.internal, *.local, *.localhost) and single-label
 * names. The server checks again (it is the real guard).
 * @param {string} hostname as URL.hostname gives it (IPv6 in brackets)
 */
export function isPrivateHost(hostname) {
  const host = str(hostname).toLowerCase().replace(/\.$/, '');
  if (!host) return true;
  if (host.startsWith('[')) {
    const ip = host.slice(1, -1);
    return ip === '::' || ip === '::1' || /^f[cd]/.test(ip) || /^fe[89ab]/.test(ip) || ip.startsWith('::ffff:');
  }
  const v4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
  if (v4) {
    const [a, b] = [Number(v4[1]), Number(v4[2])];
    return a === 0 || a === 10 || a === 127 || (a === 169 && b === 254) || (a === 172 && b >= 16 && b <= 31)
      || (a === 192 && b === 168) || (a === 100 && b >= 64 && b <= 127) || a >= 224;
  }
  if (!host.includes('.')) return true;
  return /(^|\.)(localhost|local|internal|localdomain)$/.test(host);
}

/**
 * A display name as the server wants it: one line, 1 to LABEL_MAX characters.
 * @returns {{ok: boolean, value: string, error: string|null}}
 */
export function validateLabel(v) {
  const value = line(str(v), 1000);
  if (!value) return { ok: false, value: '', error: t('pc.err.labelRequired') };
  if (value.length > LABEL_MAX) return { ok: false, value: '', error: t('pc.err.labelTooLong', { max: LABEL_MAX }) };
  return { ok: true, value, error: null };
}

/** A name compared like the server does (case, accents and repeated spaces aside). */
function foldLabel(label) {
  return str(label).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * True when another connector of the office has this name (the server refuses it: every instance
 * has its own name, « Canva Marie », « Canva Boutique »).
 * @param {string} label
 * @param {object[]} list the whole library
 * @param {string|null} [exceptId] the connector being renamed
 */
export function labelTaken(label, list, exceptId = null) {
  const f = foldLabel(label);
  return !!f && (Array.isArray(list) ? list : []).some((c) => c?.id !== exceptId && foldLabel(c?.label) === f);
}

/**
 * A free name for a new instance of `base`: itself, else « <base> 2 », « <base> 3 »… (what the add
 * window proposes; the person may change it).
 * @param {string} base
 * @param {object[]} list the whole library
 */
export function suggestLabel(base, list) {
  const b = line(str(base), LABEL_MAX) || '';
  if (!b || !labelTaken(b, list)) return b;
  for (let n = 2; n < 1000; n++) {
    const candidate = `${b.slice(0, LABEL_MAX - String(n).length - 1).trim()} ${n}`;
    if (!labelTaken(candidate, list)) return candidate;
  }
  return b;
}

/**
 * Checks the « Autre service » form like the server does: a name (1 to LABEL_MAX characters, one
 * line, no other connector of the office with it) and an https address (≤ URL_MAX, no identifier,
 * no « # », no internal host, not the office itself). The same address may be added again for
 * another account (under another name). An address on a remote catalogue service's host points to
 * its card instead (`suggestCatalog`).
 * @param {{label?: string, url?: string}} input
 * @param {{existing?: object[], catalogue?: object[], officeHosts?: string[]}} [opts] existing: the
 *   office's library (their names)
 * @returns {{ok: boolean, value: {label: string, url: string}, errors: {label?: string, url?: string},
 *   suggestCatalog: string|null}}
 */
export function validateCustomConnector(input = {}, { existing = [], catalogue = [], officeHosts = [] } = {}) {
  const errors = {};
  const named = validateLabel(input.label);
  const label = named.value;
  let url = '';
  let suggestCatalog = null;
  if (!named.ok) errors.label = named.error;
  else if (labelTaken(label, existing)) errors.label = t('pc.err.labelTaken', { label });
  const raw = str(input.url).trim();
  let u = null;
  if (!raw) errors.url = t('pc.err.urlRequired');
  else if (raw.length > URL_MAX) errors.url = t('pc.err.urlTooLong', { max: URL_MAX });
  else if (SPACE_OR_CONTROL_RE.test(raw)) errors.url = t('pc.err.urlInvalid');
  else {
    try { u = new URL(raw); } catch { errors.url = t('pc.err.urlInvalid'); }
  }
  if (u) {
    const host = u.hostname.toLowerCase();
    const office = new Set((officeHosts || []).map((x) => str(x).toLowerCase()).filter(Boolean));
    if (u.protocol !== 'https:') errors.url = t('pc.err.urlHttps');
    else if (u.username || u.password) errors.url = t('pc.err.urlCredentials');
    else if (raw.includes('#')) errors.url = t('pc.err.urlFragment');
    else if (office.has(host) || office.has(u.host.toLowerCase())) errors.url = t('pc.err.urlOffice');
    else if (isPrivateHost(host)) errors.url = t('pc.err.urlPrivate');
    else {
      const cat = (catalogue || []).find((c) => c?.host && c.host === host && c.kind !== 'plugin');
      if (cat) {
        suggestCatalog = cat.id;
        errors.url = t('pc.err.useCatalog', { name: cat.label });
      } else url = u.href;
    }
  }
  return { ok: Object.keys(errors).length === 0, value: { label, url }, errors, suggestCatalog };
}

/**
 * The store address as Shopify knows it (`xxx.myshopify.com`) from what a person types (with or
 * without https:// and path, a bare name, or admin.shopify.com/store/<name>), or '' (mirror of
 * the server's normalizeShop).
 * @param {unknown} v
 */
export function normalizeShop(v) {
  let s = str(v).trim().toLowerCase();
  if (!s || s.length > 300) return '';
  s = s.replace(/^https?:\/\//, '');
  const admin = /^admin\.shopify\.com\/store\/([a-z0-9][a-z0-9-]{0,60})(?:[/?#]|$)/.exec(s);
  if (admin) return `${admin[1]}.myshopify.com`;
  s = s.split(/[/?#]/)[0].replace(/\.$/, '');
  if (!s.includes('.')) s = `${s}.myshopify.com`;
  return SHOP_RE.test(s) ? s : '';
}

/**
 * Checks the form of a plugin like the server does: the add window (`editing` false: a name —
 * optional for Shopify, then named after the store — and the service's fields) or the settings
 * window (`editing`: the fields only). Mailgun: `apiKey` (required unless editing: empty keeps the
 * stored one), `region` us | eu, `domain` (optional). GitHub, Browserbase: `apiKey` (same rule). Vmake: `accessKey` and
 * `secretKey` (same rule, each). Shopify: `shop` (required, unless
 * `shopOptional` — the office's Shopify app is set: left empty, the store is picked in Shopify —
 * and no token is pasted), `accessToken` (required when `token` is true: « Coller un jeton
 * d’accès »; a token always needs its store). Never keeps a key anywhere.
 * @param {'mailgun'|'shopify'|'github'|'browserbase'|'vmake'} catalogId
 * @param {{label?: string, apiKey?: string, accessKey?: string, secretKey?: string, region?: string, domain?: string, shop?: string, accessToken?: string}} input
 * @param {{list?: object[], editing?: boolean, token?: boolean, shopOptional?: boolean, exceptId?: string|null}} [opts]
 * @returns {{ok: boolean, value: object, errors: Record<string, string>}}
 */
export function validatePluginForm(catalogId, input = {}, {
  list = [], editing = false, token = false, shopOptional = false, exceptId = null,
} = {}) {
  const errors = {};
  const value = {};
  if (!editing) {
    const raw = str(input.label).trim();
    if (raw || catalogId !== 'shopify') {
      const named = validateLabel(raw);
      if (!named.ok) errors.label = named.error;
      else if (labelTaken(named.value, list, exceptId)) errors.label = t('pc.err.labelTaken', { label: named.value });
      else value.label = named.value;
    }
  }
  if (catalogId === 'mailgun') {
    const key = str(input.apiKey).trim();
    if (!key && !editing) errors.apiKey = t('pc.err.keyRequired');
    else if (key && !MAILGUN_KEY_RE.test(key)) errors.apiKey = t('pc.err.keyInvalid');
    else if (key) value.apiKey = key;
    value.region = MAILGUN_REGIONS.includes(input.region) ? input.region : 'us';
    const domain = str(input.domain).trim().toLowerCase().replace(/\.$/, '');
    if (domain && !DOMAIN_RE.test(domain)) errors.domain = t('pc.err.domainInvalid');
    else value.domain = domain;
  } else if (catalogId === 'github') {
    const key = str(input.apiKey).trim();
    if (!key && !editing) errors.apiKey = t('pc.err.githubTokenRequired');
    else if (key && !GITHUB_TOKEN_RE.test(key)) errors.apiKey = t('pc.err.githubTokenInvalid');
    else if (key) value.apiKey = key;
  } else if (catalogId === 'browserbase') {
    const key = str(input.apiKey).trim();
    if (!key && !editing) errors.apiKey = t('pc.err.browserbaseKeyRequired');
    else if (key && !BROWSERBASE_KEY_RE.test(key)) errors.apiKey = t('pc.err.browserbaseKeyInvalid');
    else if (key) value.apiKey = key;
  } else if (catalogId === 'vmake') {
    for (const field of ['accessKey', 'secretKey']) {
      const key = str(input[field]).trim();
      if (!key && !editing) errors[field] = t(field === 'accessKey' ? 'pc.err.vmakeAccessKeyRequired' : 'pc.err.vmakeSecretKeyRequired');
      else if (key && !VMAKE_KEY_RE.test(key)) errors[field] = t('pc.err.vmakeKeyInvalid');
      else if (key) value[field] = key;
    }
  } else if (catalogId === 'shopify') {
    const typed = str(input.shop).trim();
    const shop = normalizeShop(typed);
    if (shop) value.shop = shop;
    else if (typed) errors.shop = t('pc.err.shopInvalid');
    else if (token || !shopOptional) errors.shop = t(token ? 'pc.err.shopForToken' : 'pc.err.shopRequired');
    if (token) {
      const tok = str(input.accessToken).trim();
      if (!tok) errors.accessToken = t('pc.err.tokenRequired');
      else if (!SHOPIFY_TOKEN_RE.test(tok)) errors.accessToken = t('pc.err.tokenInvalid');
      else value.accessToken = tok;
    }
  }
  return { ok: Object.keys(errors).length === 0, value, errors };
}

/**
 * The return from Shopify in the address (`#/connectors/<id>?shopify=ok|error|…`, the server's
 * redirect after « Se connecter avec Shopify »), or an arrival from Shopify waiting for the
 * person's choice (`#/connectors?shopify=launch&l=<id>`: with `launchId`; without a readable id,
 * `launch-invalid`): its outcome, or null.
 * @param {string} hash location.hash
 * @returns {{outcome: string, id: string|null, launchId?: string}|null}
 */
export function shopifyReturnOf(hash) {
  const raw = str(hash).replace(/^#\/?/, '');
  const [path, query = ''] = raw.split('?');
  const params = new URLSearchParams(query);
  const outcome = params.get('shopify');
  if (!SHOPIFY_OUTCOMES.includes(outcome)) return null;
  const parts = path.split('/').filter(Boolean);
  if (parts[0] !== 'connectors') return null;
  if (outcome === 'launch') {
    const launchId = params.get('l') || '';
    return LAUNCH_ID_RE.test(launchId) ? { outcome, id: null, launchId } : { outcome: 'launch-invalid', id: null };
  }
  return { outcome, id: parts[1] && LIBRARY_ID_RE.test(parts[1]) ? parts[1] : null };
}

/**
 * The address the server gave for « Se connecter avec Shopify » when it is safe to open (https,
 * no credentials, port nor fragment), else '': a store's own authorize or install page
 * (`*.myshopify.com/admin/oauth/authorize`, `…/admin/oauth/install_custom_app`,
 * `…/admin/oauth/install`), or, with no store yet, one of Shopify's install pages on
 * admin.shopify.com where the store is picked (a path with /oauth/ in it, or the Dev Dashboard's
 * link: the root with `organization_id` and `redirect=/oauth/redirect_from_developer_dashboard…`).
 * @param {unknown} url
 */
export function safeShopifyUrl(url) {
  let u;
  try { u = new URL(str(url)); } catch { return ''; }
  if (u.protocol !== 'https:' || u.username || u.password || u.port || u.hash) return '';
  let ok = false;
  if (SHOP_RE.test(u.hostname)) ok = SHOP_OAUTH_PATHS.includes(u.pathname);
  else if (u.hostname === 'admin.shopify.com') {
    ok = u.pathname.includes('/oauth/') || (u.pathname === '/' && /^\d{1,20}$/.test(u.searchParams.get('organization_id') || '')
      && (u.searchParams.get('redirect') || '').startsWith('/oauth/redirect_from_developer_dashboard'));
  }
  return ok && isSafeUrlAttr('href', u.href) ? u.href : '';
}

/**
 * Normalises GET …/shopify/launch/<id> (an arrival from Shopify, for the confirm window): the
 * store, when it ends, and the person's Shopify connectors it may go to.
 * @param {unknown} res
 * @returns {{shop: string, expiresAt: number|null, candidates: {id: string, label: string, status: string, shop: string}[]}|null}
 *   null when the store is not a store's address
 */
export function shopifyLaunchOf(res) {
  const r = res && typeof res === 'object' && !Array.isArray(res) ? res : {};
  const shop = typeof r.shop === 'string' && SHOP_RE.test(r.shop) ? r.shop : '';
  if (!shop) return null;
  const seen = new Set();
  const candidates = [];
  for (const c of Array.isArray(r.candidates) ? r.candidates : []) {
    if (!c || typeof c.id !== 'string' || !LIBRARY_ID_RE.test(c.id) || seen.has(c.id)) continue;
    seen.add(c.id);
    candidates.push({
      id: c.id, label: line(c.label, LABEL_MAX) || c.id, status: PC_STATES.includes(c.status) ? c.status : 'added',
      shop: typeof c.shop === 'string' && SHOP_RE.test(c.shop) ? c.shop : '',
    });
  }
  return { shop, expiresAt: Number.isFinite(r.expiresAt) ? r.expiresAt : null, candidates };
}

/**
 * The port, path and state the CLI's callback page must carry, read from the login page address
 * (its `redirect_uri` and `state` parameters), and the store a Shopify one is for (the page's own
 * host); null when the address does not say.
 * @param {string} authUrl
 * @returns {{port: string, state: string, path: string, shop: string}|null}
 */
export function expectedCallback(authUrl) {
  let a;
  try { a = new URL(str(authUrl)); } catch { return null; }
  const state = a.searchParams.get('state') || '';
  let port = '';
  let path = '';
  try {
    const r = new URL(a.searchParams.get('redirect_uri') || '');
    if (r.protocol === 'http:' && CALLBACK_HOSTS.includes(r.hostname)) { port = r.port; path = r.pathname; }
  } catch { /* no redirect_uri */ }
  const shop = SHOP_RE.test(a.hostname) ? a.hostname : '';
  return port || state ? { port, state, path, shop } : null;
}

/**
 * What the page that does not load starts with, from the login page address: « http://127.0.0.1:13387/ »
 * for the Shopify CLI ('' when the address does not say).
 * @param {string} authUrl
 */
export function callbackPrefix(authUrl) {
  let a;
  try { a = new URL(str(authUrl)); } catch { return ''; }
  try {
    const r = new URL(a.searchParams.get('redirect_uri') || '');
    return r.protocol === 'http:' && CALLBACK_HOSTS.includes(r.hostname) ? `${r.origin}/` : '';
  } catch {
    return '';
  }
}

/**
 * Checks the address pasted from the page that does not load (http://localhost:<port>/callback?code=…
 * &state=…, or ?error=… when the person refused; the Shopify CLI's: http://127.0.0.1:13387/auth/callback?
 * code=…&shop=…&state=…): one line, a loopback name, the login page's callback path (/callback when
 * it does not say), exactly one state and a code or an error; when the login page says which port
 * and state it expects, they must match (an address from an older attempt is refused here, with a
 * clear message), and a Shopify one must be for the same store.
 * An address without its scheme (« localhost:<port>/callback?… », as some address bars show it)
 * gets `http://` back (`value`). The server checks it again before replaying it. The value is
 * never kept anywhere.
 * @param {unknown} v
 * @param {{authUrl?: string}} [opts] the job's login page address
 * @returns {{ok: boolean, value: string, error: string|null}}
 */
export function validateCallbackUrl(v, { authUrl = '' } = {}) {
  const raw = str(v).trim();
  const value = /^(?:localhost|127\.0\.0\.1|\[::1\]):\d/i.test(raw) ? `http://${raw}` : raw;
  const fail = (key) => ({ ok: false, value: '', error: t(key) });
  if (!value) return fail('pc.cb.required');
  if (value.length > CALLBACK_MAX) return fail('pc.cb.tooLong');
  if (SPACE_OR_CONTROL_RE.test(value)) return fail('pc.cb.oneLine');
  let u;
  try { u = new URL(value); } catch { return fail('pc.cb.notLocal'); }
  if (!CALLBACK_PROTOCOLS.includes(u.protocol) || !CALLBACK_HOSTS.includes(u.hostname) || u.username || u.password) return fail('pc.cb.notLocal');
  const states = u.searchParams.getAll('state');
  const expected = expectedCallback(authUrl);
  if (u.pathname !== (expected?.path || CALLBACK_PATH) || states.length !== 1 || !states[0]
    || (!u.searchParams.get('code') && !u.searchParams.get('error'))) return fail('pc.cb.incomplete');
  if (expected && ((expected.port && u.port !== expected.port) || (expected.state && states[0] !== expected.state))) {
    return fail('pc.cb.otherAttempt');
  }
  const shop = u.searchParams.get('shop');
  if (expected?.shop && shop && normalizeShop(shop) !== expected.shop) return fail('pc.cb.otherShop');
  return { ok: true, value, error: null };
}

/**
 * The login page of a job when it is safe to link: https (or a page of the office itself, absolute
 * or as a path: the simulated login of the mock engine), accepted by dom.js, one line. Any https
 * host is possible (the server checks a catalogue service's), so the window always shows the host
 * next to the link.
 * @param {unknown} url
 * @param {{origin?: string}} [opts] the office's origin (location.origin)
 * @returns {{url: string, host: string}|null}
 */
export function safeAuthUrl(url, { origin = '' } = {}) {
  const raw = typeof url === 'string' ? url.trim() : '';
  const base = /^https?:\/\//i.test(origin) ? origin.replace(/\/+$/, '') : '';
  // A path of the office itself ("/api/connectors/library/…") is read against its origin.
  const s = base && /^\/(?![/\\])/.test(raw) ? `${base}${raw}` : raw;
  if (!s || s.length > 8000 || SPACE_OR_CONTROL_RE.test(s) || !isSafeUrlAttr('href', s)) return null;
  const own = !!base && s.startsWith(`${base}/`);
  if (!/^https:\/\//i.test(s) && !own) return null;
  const host = hostOf(s);
  return host ? { url: s, host } : null;
}

// ---------------------------------------------------------------------------------------------
// Login jobs

/** The job of a login answer (`{job}` or a bare job), normalised; null when there is none. */
export function connectJobOf(res) {
  return unwrapJob(res, '');
}

/** A job built by the page itself (starting, or a local failure with its message). */
export function localConnectJob(state, message = '') {
  return localJob('', state, message);
}

/**
 * What the status line of the connect window says about a job: the server's message when it has
 * one (already French), else a default per state.
 * @param {object|null} job connectJobOf()
 * @param {string} name the connector's display name
 * @returns {{text: string, tone: 'info'|'error'|'success'|'warn'}}
 */
export function jobText(job, name) {
  if (!job) return { text: '', tone: 'info' };
  if (job.state === 'done') {
    // A message on a finished login is a caveat (e.g. no tool found): it replaces the success line.
    return job.message ? { text: job.message, tone: 'warn' } : { text: t('pc.job.done', { name }), tone: 'success' };
  }
  const failed = ['error', 'expired', 'cancelled'].includes(job.state);
  const tone = failed || (job.state === 'waiting_code' && job.message) ? 'error' : 'info';
  if (job.message) return { text: job.message, tone };
  switch (job.state) {
    case 'starting': return { text: t('pc.job.starting'), tone };
    case 'waiting_code': return { text: t('pc.job.waiting_code'), tone };
    case 'checking': return { text: t('pc.job.checking', { name }), tone };
    case 'error': case 'expired': case 'cancelled': return { text: t(`pc.job.${job.state}`), tone };
    default: return { text: '', tone };
  }
}
